/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ErrorMessage from '../common/ErrorMessage.jsx';
import WarningMessage from '../common/WarningMessage.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import TableTitleHeader from '../common/TableTitleHeader.jsx';
import { blue, yellow } from '../../styles/standard-colors.js';
import ConfirmationModal from '../common/ConfirmationModal.jsx';
import SettingsDropdown from './SettingsDropdown.jsx';
import {
    selectNewScObj,
    updateScActiveState,
    updateScPausedState
} from '../../redux/slices/new-sc-slice.js';
import useNewScs from '../../hooks/useNewScs.js';
import CreateUpdateNewScCardForm from './CreateUpdateNewScCardForm.jsx';
import CardInfoTable from './CardInfoTable.jsx';
import SoftwareTracker from './SoftwareTracker.jsx';
import showToastPromise from '../../utils/toast-promises.js';

/**
 * Component to display information about a single SC card and manage its settings.
 *
 * @returns {JSX.Element} JSX element representing the SC card information container.
 */
const NewScCardInfoContainer = () => {
    const dispatch = useDispatch();
    const newScObj = useSelector(selectNewScObj);

    const [showDropdown, setShowDropdown] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showChangeActiveStateModal, setShowChangeActiveStateModal] =
        useState(false);
    const flagged = newScObj.daysInOnboarding >= 121;
    const {
        error,
        notificationWarning,
        loading,
        updateNewSc,
        reactivateNewSc
    } = useNewScs();

    /**
     * Handles the change in active state of the SC card.
     */
    const handleChangeActiveState = async () => {
        setShowChangeActiveStateModal(false);

        const result = await updateNewSc(newScObj.id, {
            isActive: !newScObj.isActive
        });

        if (!result?.sc) {
            return;
        }

        dispatch(updateScActiveState(result.sc.isActive));

        setShowDropdown(false);
    };

    const handleReactivatePausedSC = async () => {
        const promise = reactivateNewSc(newScObj.id);

        try {
            const updatedNewSc = await showToastPromise(promise, {
                loading: 'Reactivating SC...',
                success: 'SC successfully reactivated!',
                duration: 3000
            });

            if (!updatedNewSc) return;
            dispatch(updateScPausedState(updatedNewSc.isPaused));
            dispatch(updateScActiveState(updatedNewSc.isActive));
            setShowDropdown(false);
        } catch (err) {
            console.error('Reactivation failed:', err);
        }
    };

    return (
        <div className="office-container">
            {newScObj.isPaused ? (
                <TableTitleHeader
                    includeBackArrow={true}
                    title={`${newScObj.lastName}, ${newScObj.firstName}`}
                    hasSettingsGear={true}
                    settingsGearColor={yellow}
                    setShowDropdown={setShowDropdown}
                    showDropdown={showDropdown}
                    customContainerStyles={{ marginLeft: '30px' }}
                />
            ) : flagged && newScObj.isActive ? (
                <TableTitleHeader
                    includeBackArrow={true}
                    title={`${newScObj.lastName}, ${newScObj.firstName}`}
                    hasSettingsGear={true}
                    settingsGearColor={yellow}
                    setShowDropdown={setShowDropdown}
                    showDropdown={showDropdown}
                    hasActiveStateDot={true}
                    activeStateDotSize={40}
                    isRetired={true} // This is just to force it to be red, the object is not retired
                    customContainerStyles={{ marginLeft: '30px' }}
                />
            ) : (
                <TableTitleHeader
                    includeBackArrow={true}
                    title={`${newScObj.lastName}, ${newScObj.firstName}`}
                    hasSettingsGear={true}
                    settingsGearColor={yellow}
                    setShowDropdown={setShowDropdown}
                    showDropdown={showDropdown}
                    customContainerStyles={{ marginLeft: '30px' }}
                />
            )}

            {loading && <LoadingSpinner />}
            {error && <ErrorMessage message={error.message} />}
            {notificationWarning && (
                <WarningMessage message={notificationWarning} />
            )}
            {newScObj.splashtopSyncError && (
                <WarningMessage message="⚠️ Splashtop Sync Error — please move this SC to the Success Consultants group in Splashtop manually." />
            )}
            {newScObj.isPaused && (
                <div style={{ marginLeft: '30px' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '2em' }}>
                        **NEW SC IS PAUSED**
                    </h3>
                </div>
            )}

            {showDropdown && (
                <SettingsDropdown
                    showEdit={showEdit}
                    setShowEdit={setShowEdit}
                    showChangeActiveStateModal={showChangeActiveStateModal}
                    setShowChangeActiveStateModal={
                        setShowChangeActiveStateModal
                    }
                    onReactivatePausedSC={handleReactivatePausedSC}
                />
            )}
            {showEdit ? (
                <CreateUpdateNewScCardForm
                    objectToUpdate={newScObj}
                    setShowEdit={setShowEdit}
                />
            ) : (
                <CardInfoTable />
            )}
            <br />
            <TableTitleHeader
                title="Practice Management Software"
                underlineColor={blue}
                customContainerStyles={{
                    marginLeft: '30px',
                    justifyContent: 'center'
                }}
            />
            <SoftwareTracker />
            {showChangeActiveStateModal && (
                <ConfirmationModal
                    title={
                        newScObj.isActive
                            ? 'Retire SC Card'
                            : 'Re-activate SC Card'
                    }
                    message={
                        newScObj.isActive
                            ? 'Are you sure you want to retire this SC card?'
                            : 'Are you sure you want to re-activate this SC card?'
                    }
                    onConfirm={handleChangeActiveState}
                    setShowModal={setShowChangeActiveStateModal}
                />
            )}
        </div>
    );
};

export default NewScCardInfoContainer;
